import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { PercentContext, PercentQuestionOptions, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { formatDollars } from "./MoneyGenerator.ts";
import { withThousands } from "./PlaceValueGenerator.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { percentPrompts as en } from "../Files/Prompts/en/percent.ts";
import { percentPrompts as es } from "../Files/Prompts/es/percent.ts";
import { percentPrompts as fr } from "../Files/Prompts/fr/percent.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * Percent. See ADR 0034.
 *
 * Percent is notation over values that already exist - 45% is 0.45 is 9/20 -
 * so there is no percent type and no percent arithmetic here, only conversion
 * and ordinary whole-number work.
 *
 * The rule that shapes the file is ADR 0034 §3: every answer is a whole
 * number, because `33% of 50` is 16.5, which is a decimals question, and
 * Decimals is already a format with its own place-count setting. So the
 * percentage and the base are drawn **together** - never independently - and
 * nothing is rejected after the fact.
 *
 * That constraint is invisible in the output. Nothing on a generated sheet
 * says the percentages were chosen to divide cleanly, so widening
 * PERCENTAGES below will silently start printing decimals;
 * `PercentGenerator.test.ts` is what says otherwise.
 */

/**
 * Percentages a sheet may use, with the multiple each needs its base to be.
 *
 * 20% of a multiple of 5 is whole; 20% of 63 is not. Keeping the requirement
 * beside the percentage is what makes the pair drawable rather than testable.
 */
const PERCENTAGES: Array<{ percent: number; baseMultiple: number }> = [
    { percent: 10, baseMultiple: 10 },
    { percent: 20, baseMultiple: 5 },
    { percent: 25, baseMultiple: 4 },
    { percent: 30, baseMultiple: 10 },
    { percent: 40, baseMultiple: 5 },
    { percent: 50, baseMultiple: 2 },
    { percent: 60, baseMultiple: 5 },
    { percent: 70, baseMultiple: 10 },
    { percent: 75, baseMultiple: 4 },
    { percent: 80, baseMultiple: 5 },
    { percent: 90, baseMultiple: 10 },
];

/** Benchmark percentages whose fraction and decimal forms are worth knowing. */
const BENCHMARKS = [10, 20, 25, 40, 50, 60, 75, 80, 90];

function questionTypes(question: PercentQuestionOptions): PercentQuestionOptions[] {
    if (question === PercentQuestionOptions.Mixed) {
        return [
            PercentQuestionOptions.Convert,
            PercentQuestionOptions.PercentOf,
            PercentQuestionOptions.FindThePercent,
            PercentQuestionOptions.PercentChange,
        ];
    }
    return [question];
}

function usableMax(maxValue: number): number {
    return Math.max(20, Math.floor(maxValue) || 100);
}

/**
 * A percentage, and a base it divides exactly, both inside `max`.
 *
 * The base has a floor that scales with the setting. Drawing the multiplier
 * from 1 put "3 is what percent of 5?" on a sheet whose largest number was
 * 200 - a legitimate question, and not one that belongs beside "what is 80% of
 * 155?". A quarter of the range keeps the easy end without the trivial one.
 */
function percentageAndBase(max: number): { percent: number; base: number } {
    const choice = PERCENTAGES[getRandomInt(0, PERCENTAGES.length - 1)];
    const largestMultiplier = Math.max(1, Math.floor(max / choice.baseMultiple));
    const smallestMultiplier = Math.max(1, Math.floor(largestMultiplier / 4));
    const base = choice.baseMultiple * getRandomInt(smallestMultiplier, largestMultiplier);
    return { percent: choice.percent, base };
}

/** 0.45 printed without a trailing zero problem: 0.4, not 0.40. */
function asDecimal(percent: number): string {
    return String(percent / 100);
}

/**
 * A conversion, in one direction (ADR 0034 §4).
 *
 * The fraction form keeps the denominator the question used - `40% = 40/100`,
 * not `2/5`. Asking for it simplified would make one question test two skills,
 * and a student who converts correctly and does not simplify would be wrong
 * for a reason this sheet did not set out to test.
 */
function buildConvert(language: PrintedLanguage): { prompt: string; answer: string } {
    const t = PROMPTS[language];
    const percent = BENCHMARKS[getRandomInt(0, BENCHMARKS.length - 1)];
    switch (getRandomInt(0, 2)) {
        case 0:
            return { prompt: t.convertToDecimal(percent), answer: asDecimal(percent) };
        case 1:
            return {
                prompt: t.convertToFraction(percent),
                answer: `${percent}/100`,
            };
        default:
            return { prompt: t.convertToPercent(asDecimal(percent)), answer: String(percent) };
    }
}

/**
 * Money questions (ADR 0120).
 *
 * The rules above (§3 of 0034: whole answers, no `$`) hold for 0034's four
 * types and their Mixed. These four members break both on purpose: the cents
 * are the unit of a tax question, not a decimals exercise laid on top. What
 * keeps them exact is the same device as before - draw the pair together - but
 * in integer cents and tenths of a percent, so nothing is rounded and nothing
 * is rejected after the fact.
 *
 * - A rate `q` is in tenths of a percent: 7.5% is 75, 6% is 60.
 * - A price of `p` cents at rate `q` has `p * q / 1000` cents of tax, which is
 *   whole exactly when `p * q` is a multiple of 1000. The rate is drawn first
 *   (it is what the sheet practises: drawn uniformly over every qualifying pair,
 *   25% would have twelve times the prices 6% has), then a price those cents allow.
 * - Interest is on a principal in tens of dollars, which is always whole.
 */

/** Which contexts each money member deals, in the order they are listed. */
const MONEY_CONTEXTS: Partial<Record<PercentQuestionOptions, PercentContext[]>> = {
    [PercentQuestionOptions.TaxAndTip]: ['tax', 'tip'],
    [PercentQuestionOptions.DiscountAndMarkup]: ['discount', 'markup'],
    [PercentQuestionOptions.SimpleInterest]: ['interest'],
    [PercentQuestionOptions.PercentWordProblems]: ['tax', 'tip', 'discount', 'markup', 'interest'],
};

/** Tenths of a percent from `from` to `to` in steps of `step`. */
function tenths(from: number, to: number, step: number): number[] {
    const out: number[] = [];
    for (let q = from; q <= to; q += step) out.push(q);
    return out;
}

/** The rates each context offers, in tenths of a percent (ADR 0120 §3). */
export const PERCENT_RATES: Record<PercentContext, number[]> = {
    tax: tenths(30, 100, 5), // 3% to 10% by halves
    tip: [100, 150, 180, 200, 250],
    discount: [...tenths(100, 500, 50), 600, 700, 750],
    markup: [...tenths(100, 500, 50), 600, 750, 1000],
    interest: tenths(10, 80, 5), // 1% to 8% by halves
};

/** The least a principal or price may be: a quarter of the dial, in whole dollars. */
export function moneyFloorDollars(dial: number): number {
    return Math.ceil(dial / 4);
}

/** The simple interest question's years, whole (ADR 0120 §3). */
export const INTEREST_YEARS = { min: 2, max: 10 };

function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
}

/** 45 -> "4.5", 60 -> "6". Integer arithmetic, so no float ever reaches a page. */
export function formatRate(q: number): string {
    return q % 10 === 0 ? String(q / 10) : `${Math.trunc(q / 10)}.${q % 10}`;
}

/**
 * A price in cents, uniform over those in [a quarter of the dial, the dial]
 * that rate `q` keeps exact.
 */
function exactPrice(q: number, dial: number): number {
    const high = dial * 100;
    const low = Math.ceil(high / 4);
    const step = 1000 / gcd(q, 1000);
    const first = Math.ceil(low / step);
    const last = Math.max(first, Math.floor(high / step));
    return step * getRandomInt(first, last);
}

function pick<T>(items: T[]): T {
    return items[getRandomInt(0, items.length - 1)];
}

function buildMoney(
    context: PercentContext,
    dial: number,
    language: PrintedLanguage
): { prompt: string; answer: string } {
    const t = PROMPTS[language];
    const q = pick(PERCENT_RATES[context]);
    const rate = formatRate(q);

    if (context === 'interest') {
        const lowTens = Math.max(1, Math.ceil(moneyFloorDollars(dial) / 10));
        const highTens = Math.max(lowTens, Math.floor(dial / 10));
        const principal = 10 * getRandomInt(lowTens, highTens);
        const years = getRandomInt(INTEREST_YEARS.min, INTEREST_YEARS.max);
        const borrowed = getRandomInt(0, 1) === 1;
        // Dollars x tenths of a percent x years / 10, in cents; whole because
        // the principal is a multiple of ten.
        const interest = (principal * q * years) / 10;
        return {
            prompt: t.simpleInterest(`$${withThousands(principal)}`, rate, years, borrowed),
            answer: formatDollars(interest),
        };
    }

    const price = exactPrice(q, dial);
    const change = (price * q) / 1000;
    const priced = formatDollars(price);
    switch (context) {
        case 'tip':
            return {
                prompt: t.tip(pick(t.tipThings), priced, rate),
                answer: formatDollars(price + change),
            };
        case 'discount':
            return {
                prompt: t.discount(pick(t.retailThings), priced, rate),
                answer: formatDollars(price - change),
            };
        case 'markup':
            return {
                prompt: t.markup(pick(t.retailThings), priced, rate),
                answer: formatDollars(price + change),
            };
        default:
            return {
                prompt: t.salesTax(pick(t.retailThings), priced, rate),
                answer: formatDollars(price + change),
            };
    }
}

function buildProblem(
    question: PercentQuestionOptions,
    max: number,
    language: PrintedLanguage,
    context?: PercentContext
): { prompt: string; answer: string } {
    const t = PROMPTS[language];
    if (context) {
        return buildMoney(context, max, language);
    }
    switch (question) {
        case PercentQuestionOptions.FindThePercent: {
            const { percent, base } = percentageAndBase(max);
            const part = (base * percent) / 100;
            return {
                prompt: t.findThePercent(part, base),
                answer: `${percent}%`,
            };
        }
        case PercentQuestionOptions.PercentChange: {
            const { percent, base } = percentageAndBase(max);
            const change = (base * percent) / 100;
            const increasing = getRandomInt(0, 1) === 0;
            // No currency symbol: this is a number, not an amount of money,
            // and Money is a format with coins and cents (ADR 0034 §5).
            return {
                prompt: increasing
                    ? t.percentChangeRise(base, percent)
                    : t.percentChangeFall(base, percent),
                answer: String(increasing ? base + change : base - change),
            };
        }
        case PercentQuestionOptions.Convert:
            return buildConvert(language);
        default: {
            const { percent, base } = percentageAndBase(max);
            return {
                prompt: t.percentOf(percent, base),
                answer: String((base * percent) / 100),
            };
        }
    }
}

/**
 * One percent problem. Always succeeds - the pair is drawn so it divides, so
 * there is nothing to reject.
 *
 * A money member (ADR 0120) is set in one context at a time; called without
 * one, it draws one of its own, so this never fails on any member.
 */
export function buildPercentProblem(
    question: PercentQuestionOptions,
    maxValue: number,
    key: number,
    language: PrintedLanguage = 'en',
    context?: PercentContext
): ProblemData {
    const contexts = MONEY_CONTEXTS[question];
    const chosen = context ?? (contexts ? pick(contexts) : undefined);
    const built = buildProblem(question, usableMax(maxValue), language, chosen);
    return {
        key,
        numbers: [],
        operator: "",
        display: [built.prompt, built.answer],
        percentQuestion: question,
        ...(chosen ? { percentContext: chosen } : {}),
    };
}

/** What a sheet rotates over: a question type, or for a money member one of its contexts. */
interface PercentChoice {
    question: PercentQuestionOptions;
    context?: PercentContext;
}

function choicesFor(question: PercentQuestionOptions): PercentChoice[] {
    const contexts = MONEY_CONTEXTS[question];
    if (contexts) {
        return contexts.map((context) => ({ question, context }));
    }
    return questionTypes(question).map((type) => ({ question: type }));
}

/** A page-shaped set of percent problems. */
export function generatePercentPages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.PercentQuestion ?? PercentQuestionOptions.Mixed;
    const maxValue = settings.PercentMaxValue ?? 100;
    const language = printedLanguageOf(settings);
    return generateProblemPages({
        settings,
        choices: choicesFor(question),
        build: (choice, key) =>
            buildPercentProblem(choice.question, maxValue, key, language, choice.context),
        fingerprint: (problem) => problem.display![0],
        // A money member hands each printed page its own even share of its
        // contexts (ADR 0120 §1). Only those: the original types' sequences stay as they were.
        dealPerPage: MONEY_CONTEXTS[question] !== undefined,
    });
}
